import { TFunction } from "i18next";

const tBase = "orderPageWidgets.orderDetails.receipt";

type ReceiptErrorMapping = {
	test: RegExp;
	code: string;
	fallback: string;
};

const RECEIPT_ERROR_MAPPINGS: ReceiptErrorMapping[] = [
	{
		test: /^MISSING_DRIVER_TAX_ID\b/i,
		code: "MISSING_DRIVER_TAX_ID",
		fallback: "У водителя не указан ИНН (IDNP)",
	},
	{
		test: /^MISSING_FLEET_IDNO\b/i,
		code: "MISSING_FLEET_IDNO",
		fallback: "Машина не принадлежит автопарку с валидным IDNO",
	},
	{
		test: /^MISSING_CASH_REGISTER\b/i,
		code: "MISSING_CASH_REGISTER",
		fallback: "Водитель/машина не привязан к кассе",
	},
	{
		test: /check car id|pos id from db|vehicle.*not.*regist/i,
		code: "ECC_CAR_NOT_REGISTERED",
		fallback:
			"Чек не отправлен — номер машины не зарегистрирован в ECC (POS не найден). Укажите госномер из карточки транспорта и зарегистрируйте его у провайдера",
	},
	{
		test: /check driver|invalid driver|invalid idnp/i,
		code: "ECC_DRIVER_NOT_REGISTERED",
		fallback:
			"Чек не отправлен — водитель не зарегистрирован в ECC. Укажите ИНН (IDNP) в карточке водителя",
	},
	{
		test: /network error|econnrefused|etimedout|fetch failed|socket hang up/i,
		code: "ECC_NETWORK_ERROR",
		fallback: "Чек не отправлен — ошибка связи с ECC. Попробуйте позже",
	},
	{
		test: /not ready after|download error|http 5\d\d/i,
		code: "ECC_DOWNLOAD_FAILED",
		fallback: "Чек зарегистрирован в ECC, но не удалось скачать файл",
	},
];

function looksLikeHtml(value: string): boolean {
	return (
		/<!DOCTYPE/i.test(value) ||
		/<html[\s>]/i.test(value) ||
		/<\/html>/i.test(value)
	);
}

function firstLineOf(text: string): string {
	return text.split(/\r?\n/, 1)[0].trim();
}

function restLinesOf(text: string): string {
	const match = text.match(/\r?\n([\s\S]*)$/);
	return match?.[1]?.trim() ?? "";
}

function extractEccMessage(lastError: string): string {
	const bodyMatch = lastError.match(/body=(\{[\s\S]*\})\s*$/);
	if (bodyMatch) {
		try {
			const parsed = JSON.parse(bodyMatch[1]) as { message?: string };
			if (typeof parsed.message === "string" && parsed.message.trim()) {
				return parsed.message.trim();
			}
		} catch {
			// ignore malformed JSON tail
		}
	}

	const prefixed = lastError.match(
		/^Receipt registration failed(?: \(HTTP \d+\))?:\s*(.+)$/i,
	);
	if (prefixed?.[1]) {
		return prefixed[1].trim();
	}

	return lastError.trim();
}

function translateReceiptError(
	t: TFunction,
	code: string,
	fallback: string,
): string {
	return t(`${tBase}.errors.${code}`, { defaultValue: fallback });
}

export function extractReceiptErrorMessage(lastError: string): string {
	return extractEccMessage(firstLineOf(lastError));
}

function resolveShortReceiptLastErrorMessage(
	t: TFunction,
	lastError: string,
): string {
	if (looksLikeHtml(lastError)) {
		if (
			/cloudflare|you have been blocked|attention required/i.test(
				lastError,
			)
		) {
			const http = lastError.match(/HTTP\s+(\d+)/i)?.[1];
			return http
				? `Cloudflare blocked the request (HTTP ${http})`
				: "Cloudflare blocked the request";
		}
		return translateReceiptError(
			t,
			"GENERIC",
			"Не удалось отправить чек в ECC. Попробуйте повторить или обратитесь к администратору",
		);
	}

	const firstLine = firstLineOf(lastError);
	const message = extractEccMessage(firstLine);

	for (const mapping of RECEIPT_ERROR_MAPPINGS) {
		if (mapping.test.test(message) || mapping.test.test(firstLine)) {
			return translateReceiptError(t, mapping.code, mapping.fallback);
		}
	}

	if (message && message !== firstLine) {
		return message;
	}

	if (message.length > 0 && message.length <= 240) {
		return message;
	}

	return translateReceiptError(
		t,
		"GENERIC",
		"Не удалось отправить чек в ECC. Попробуйте повторить или обратитесь к администратору",
	);
}

export function resolveReceiptLastErrorMessage(
	t: TFunction,
	lastError?: string | null,
): string | null {
	if (!lastError) return null;
	return resolveShortReceiptLastErrorMessage(t, lastError);
}

export function resolveReceiptArchiveReasonMessage(
	t: TFunction,
	lastError?: string | null,
): string | null {
	if (!lastError) return null;

	if (looksLikeHtml(lastError)) {
		const short = resolveShortReceiptLastErrorMessage(t, lastError);
		const http = lastError.match(/HTTP\s+(\d+)/i)?.[1];
		const lines = [short];
		if (http) lines.push(`HTTP ${http}`);
		lines.push(
			/cloudflare|you have been blocked|attention required/i.test(
				lastError,
			)
				? "Ответ ECC — HTML Cloudflare, не JSON API"
				: "Ответ ECC — HTML-страница, не JSON API",
		);
		return lines.join("\n");
	}

	const first = firstLineOf(lastError);
	const short = resolveShortReceiptLastErrorMessage(t, lastError);
	const rest = restLinesOf(lastError);
	const lines = [short];

	if (
		short !== first &&
		!/^MISSING_(FLEET_IDNO|DRIVER_TAX_ID)\b/i.test(first)
	) {
		const original = extractEccMessage(first);
		if (original && !short.includes(original)) {
			lines.push(original);
		}
	}

	if (rest) {
		lines.push(rest);
	}

	return lines.join("\n");
}
